import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePinTrigger } from "../hooks/usePinTrigger";
import OTPModal from "../modal/OTPModal";
import {
  closeKioskApp,
  isElectronKiosk,
  isStandaloneDisplay,
} from "../utils/kioskRuntime";
import { toast } from "react-toastify";

const isAppHomePath = (pathname: string): boolean =>
  pathname === "/" || pathname === "";

const GlobalPinHandler = () => {
  const [showPinModal, setShowPinModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const excludeSelectors = [
    ".drawingCanvas",
    ".imagaeContainerCanvas",
    ".textContainerCanvas",
    "button",
    "input",
    "select",
    "textarea",
    "a",
    ".deleteIconCanva",
    ".rotateIconCanva",
    ".deleteIconCanvaTxt",
    ".rotateIconCanvaTxt",
  ];

  const handlePinTrigger = useCallback(() => {
    console.log(
      "[GlobalPinHandler] PIN trigger detected on page:",
      pathnameRef.current
    );
    setShowPinModal(true);
  }, []);

  usePinTrigger({
    onTrigger: handlePinTrigger,
    excludeSelectors,
    clickDelay: 500,
  });

  const handlePinModalClose = () => {
    setShowPinModal(false);
  };

  const closeApp = useCallback(() => {
    try {
      interface AndroidWindow extends Window {
        Android?: {
          finish?: () => void;
          finishAffinity?: () => void;
          exitApp?: () => void;
          finishActivity?: () => void;
          finishAndRemoveTask?: () => void;
          moveTaskToBack?: (nonRoot: boolean) => void;
          homeButton?: () => void;
          minimizeApp?: () => void;
          closeApp?: () => void;
          closeCustomTab?: () => void;
          exitKiosk?: () => void;
        };
        webkit?: {
          messageHandlers?: {
            closeApp?: { postMessage?: (message: unknown) => void };
            finish?: { postMessage?: (message: unknown) => void };
            exitApp?: { postMessage?: (message: unknown) => void };
          };
        };
        android?: {
          app?: {
            exit?: () => void;
            close?: () => void;
          };
        };
      }

      const win = window as unknown as AndroidWindow;
      const userAgent = navigator.userAgent;
      const isAndroid = /Android/i.test(userAgent);
      const isWindows = /Windows/i.test(userAgent);

      // Electron / shell kiosk — quit process so device shows launcher home.
      if (isElectronKiosk()) {
        closeKioskApp();
        toast.success("PIN verified. Closing app...");
        return;
      }

      // Installed PWA / fullscreen display — try native bridges then leave the app.
      if (isStandaloneDisplay() || isAndroid) {
        const androidMethods = [
          () => win.Android?.exitKiosk?.(),
          () => win.Android?.finishAndRemoveTask?.(),
          () => win.Android?.finishAffinity?.(),
          () => win.Android?.finish?.(),
          () => win.Android?.exitApp?.(),
          () => win.Android?.finishActivity?.(),
          () => win.Android?.closeApp?.(),
          () => win.Android?.homeButton?.(),
          () => win.Android?.moveTaskToBack?.(true),
          () => win.Android?.minimizeApp?.(),
          () => (window.navigator as Navigator & { app?: { exitApp?: () => void } }).app?.exitApp?.(),
          () => win.android?.app?.exit?.(),
          () => win.android?.app?.close?.(),
          () =>
            win.webkit?.messageHandlers?.closeApp?.postMessage?.({
              action: "close",
            }),
          () =>
            win.webkit?.messageHandlers?.exitApp?.postMessage?.({
              action: "exit",
            }),
          () => (window as Window & { kioskAPI?: { close?: () => void; systemExit?: () => void } }).kioskAPI?.systemExit?.(),
          () => (window as Window & { kioskAPI?: { close?: () => void } }).kioskAPI?.close?.(),
        ];

        androidMethods.forEach((method, index) => {
          window.setTimeout(() => {
            try {
              method();
            } catch {
              /* try next */
            }
          }, index * 80);
        });

        window.setTimeout(() => {
          try {
            window.close();
          } catch {
            /* ignore */
          }
        }, androidMethods.length * 80 + 50);

        toast.success("PIN verified. Closing app...");
        return;
      }

      if (isWindows) {
        closeKioskApp();
        try {
          window.close();
        } catch {
          /* ignore */
        }
        toast.success("PIN verified. Closing app...");
        return;
      }

      try {
        window.close();
      } catch {
        /* ignore */
      }
    } catch (error) {
      console.error("[GlobalPinHandler] Error closing app:", error);
    }
  }, []);

  const handlePinSuccess = useCallback(() => {
    console.log("[GlobalPinHandler] PIN verified successfully");
    setShowPinModal(false);

    const onHome = isAppHomePath(pathnameRef.current);
    const runningInKioskShell =
      isElectronKiosk() || isStandaloneDisplay();

    // Installed kiosk/PWA: always exit to the device/launcher home — never only
    // navigate to the in-app "/" route.
    if (runningInKioskShell || onHome) {
      console.log(
        "[GlobalPinHandler] Closing app to device home (kiosk/home)",
        { onHome, runningInKioskShell, path: pathnameRef.current }
      );
      closeApp();
      return;
    }

    console.log("[GlobalPinHandler] Browser session - navigating to app home");
    navigate("/", { replace: true });
  }, [closeApp, navigate]);

  return (
    <>
      {showPinModal && (
        <OTPModal
          show={showPinModal}
          onHide={handlePinModalClose}
          onSuccess={handlePinSuccess}
        />
      )}
    </>
  );
};

export default GlobalPinHandler;
